import React, { useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface LandingPageProps {
    onGetStarted: () => void;
    onLogin: () => void;
    deferredPrompt: BeforeInstallPromptEvent | null;
    isInstallPromptAvailable: boolean;
    onInstallClick: () => Promise<void>;
    apkUrl?: string;
}

export const LandingPage: React.FC<LandingPageProps> = ({
    onGetStarted,
    onLogin,
    deferredPrompt,
    isInstallPromptAvailable,
    onInstallClick,
    apkUrl,
}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const features = [
        {
            icon: '📐',
            title: 'Mesures précises',
            description: 'Enregistrez toutes les mesures de vos clients par type de vêtement',
        },
        {
            icon: '👥',
            title: 'Gestion clients',
            description: 'Organisez vos clients avec leurs coordonnées et historique',
        },
        {
            icon: '📱',
            title: 'Mode hors-ligne',
            description: "Travaillez même sans internet, vos données se synchronisent automatiquement",
        },
        {
            icon: '📄',
            title: 'Docs Professionnels',
            description: 'Générez des fiches de mesures et reçus A6 en un clic',
        },
        {
            icon: '🔄',
            title: 'Synchronisation',
            description: 'Retrouvez vos données sur tous vos appareils',
        },
        {
            icon: '📊',
            title: 'Suivi commandes',
            description: 'Gérez les commandes, échéances et paiements de vos clients',
        },
        {
            icon: '📉',
            title: 'Analyse d\'activité',
            description: 'Statistiques clients, revenus et évolution par mois',
        },
        {
            icon: '🔔',
            title: 'Rappels automatiques',
            description: 'Alertes pour ne jamais rater une date de livraison',
        },
    ];

    const steps = [
        { number: '1', title: 'Créez votre compte', description: 'Inscription gratuite en 30 secondes' },
        { number: '2', title: 'Ajoutez vos clients', description: 'Importez ou créez vos fiches clients' },
        { number: '3', title: 'Prenez les mesures', description: 'Enregistrez facilement les mesures' },
        { number: '4', title: 'Travaillez partout', description: 'Accédez à vos données où que vous soyez' },
    ];

    const testimonials = [
        {
            name: 'Awa',
            role: 'Couturière, Cotonou',
            text: "Avant je perdais mes papiers avec les mesures. Maintenant tout est dans mon téléphone !",
        },
        {
            name: 'Kofi',
            role: 'Tailleur, Lomé',
            text: "L'application est simple à utiliser. Même sans internet, je peux voir les mesures de mes clients.",
        },
        {
            name: 'Aminata',
            role: 'Styliste, Dakar',
            text: 'Le suivi des commandes et paiements me fait gagner un temps fou.',
        },
    ];

    const pricing = [
        {
            name: 'Gratuit',
            price: '0 FCFA',
            priceEur: null,
            priceUsd: null,
            period: 'pour toujours',
            features: ['3 clients maximum', 'Mesures illimitées', 'Mode hors-ligne'],
            highlight: false,
            cta: 'Commencer gratuitement',
            promo: null,
        },
        {
            name: 'Starter',
            price: '250 FCFA',
            priceEur: '≈ 0,38 €',
            priceUsd: '0,41 $',
            period: 'par mois',
            features: ['50 clients', '50 Mo stockage', 'Synchronisation cloud'],
            highlight: false,
            cta: 'Choisir Starter',
            promo: null,
        },
        {
            name: 'Essentiel',
            price: '2 500 FCFA',
            priceEur: '≈ 3,81 €',
            priceUsd: '4,07 $',
            period: 'pour 6 mois',
            features: ['200 clients', '100 Mo stockage', 'Export PDF/CSV', 'Bons de Commande', 'Dashboard statistiques'],
            highlight: true,
            cta: 'Choisir Essentiel',
            promo: '🔥 -40%',
        },
        {
            name: 'Pro',
            price: '5 000 FCFA',
            priceEur: '≈ 7,62 €',
            priceUsd: '8,14 $',
            period: 'pour 1 an',
            features: [
                '1 000 clients', 
                '500 Mo stockage', 
                'Reçus & Bons (Premium)', 
                'Reçu Rapide (1-clic)',
                'Suivi des commandes', 
                'Dashboard & Notifications'
            ],
            highlight: false,
            cta: 'Choisir Pro',
            promo: '⭐ -50%',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
                <div className="max-w-6xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <img src="/logo.svg" alt="Logo" className="w-10 h-10 rounded-xl" />
                            <div>
                                <h1 className="text-lg font-bold text-gray-900">Carnet de Mesures</h1>
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider hidden sm:block">Atelier numérique</p>
                            </div>
                        </div>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center gap-4">
                            {apkUrl && (
                                <a
                                    href={apkUrl}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition border border-indigo-200"
                                >
                                    <span>📥</span> APK Android
                                </a>
                            )}
                            {isInstallPromptAvailable && (
                                <button
                                    onClick={onInstallClick}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition"
                                >
                                    <span>📱</span> Version Web
                                </button>
                            )}
                            <button
                                onClick={onLogin}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
                            >
                                Se connecter
                            </button>
                            <button
                                onClick={onGetStarted}
                                className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 transition-all"
                            >
                                Commencer
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2 text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
                            {apkUrl && (
                                <a
                                    href={apkUrl}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl shadow-md"
                                >
                                    <span>📥</span> Télécharger APK (Android)
                                </a>
                            )}
                            {isInstallPromptAvailable && (
                                <button
                                    onClick={() => { onInstallClick(); setMobileMenuOpen(false); }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-xl"
                                >
                                    <span>📱</span> Installer Version Web (PWA)
                                </button>
                            )}
                            <button
                                onClick={() => { onLogin(); setMobileMenuOpen(false); }}
                                className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl"
                            >
                                Se connecter
                            </button>
                            <button
                                onClick={() => { onGetStarted(); setMobileMenuOpen(false); }}
                                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl"
                            >
                                Créer un compte gratuit
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-12 md:py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Votre atelier,<br />
                            <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                                toujours dans votre poche
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                            Fini les papiers perdus ! Gérez les mesures de vos clients, suivez vos commandes et travaillez même sans internet.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            {apkUrl && (
                                <a
                                    href={apkUrl}
                                    className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-lg font-bold rounded-2xl shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 transition-all transform hover:scale-105"
                                >
                                    <span className="text-2xl">📥</span> Télécharger l'App (APK)
                                </a>
                            )}
                            <button
                                onClick={onGetStarted}
                                className={`w-full sm:w-auto px-8 py-4 ${apkUrl ? 'bg-white text-gray-700 border-2 border-gray-100' : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'} text-lg font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105`}
                            >
                                {apkUrl ? 'Se connecter' : 'Commencer'}
                            </button>
                            {isInstallPromptAvailable && !apkUrl && (
                                <button
                                    onClick={onInstallClick}
                                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-white text-indigo-600 text-lg font-semibold rounded-2xl border-2 border-indigo-200 hover:border-indigo-400 transition-all"
                                >
                                    <span className="text-2xl">📱</span> Installer l'app
                                </button>
                            )}
                        </div>
                        <p className="mt-4 text-sm text-gray-500">
                            ✅ Gratuit pour 3 clients • ✅ Aucune carte bancaire requise
                        </p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 md:py-24 px-4 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Tout ce dont vous avez besoin
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Une application complète pour gérer votre atelier de couture
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all"
                            >
                                <div className="text-4xl mb-4">{feature.icon}</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-indigo-600 to-blue-700">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Simple comme bonjour
                        </h2>
                        <p className="text-lg text-indigo-100 max-w-2xl mx-auto">
                            Commencez à utiliser l'application en quelques minutes
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {steps.map((step, index) => (
                            <div key={index} className="text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center text-3xl font-bold text-white">
                                    {step.number}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                                <p className="text-indigo-100">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 md:py-24 px-4 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Ils nous font confiance
                        </h2>
                        <p className="text-lg text-gray-600">
                            Des artisans comme vous utilisent Carnet de Mesures chaque jour
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100">
                                <div className="flex items-center gap-1 text-amber-400 mb-4">
                                    {'★★★★★'.split('').map((star, i) => <span key={i}>{star}</span>)}
                                </div>
                                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                                <div>
                                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <style>{`
                @keyframes floatCard {
                    0%, 100% { transform: translateY(0) scale(1.05); }
                    50% { transform: translateY(-12px) scale(1.05); }
                }
            `}</style>
            <section className="py-16 md:py-24 px-4 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Tarifs simples et transparents
                        </h2>
                        <p className="text-lg text-gray-600">
                            Commencez gratuitement, évoluez quand vous êtes prêt
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                        {pricing.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative p-5 rounded-2xl ${plan.highlight
                                    ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white scale-105 shadow-xl shadow-indigo-500/30'
                                    : 'bg-white border border-gray-200'
                                    }`}
                                style={plan.highlight ? {
                                    animation: 'floatCard 3s ease-in-out infinite',
                                } : undefined}
                            >
                                {plan.promo && (
                                    <div className="absolute -top-3 right-4">
                                        <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-bold shadow-lg">
                                            {plan.promo}
                                        </span>
                                    </div>
                                )}
                                {plan.highlight && (
                                    <div className="text-center mb-3">
                                        <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase">
                                            Populaire
                                        </span>
                                    </div>
                                )}
                                <h3 className={`text-xl font-bold mb-2 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                                    {plan.name}
                                </h3>
                                <div className="mb-4">
                                    <span className={`text-3xl font-bold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                                        {plan.price}
                                    </span>
                                    <span className={`text-sm ${plan.highlight ? 'text-indigo-100' : 'text-gray-500'}`}>
                                        {' '}{plan.period}
                                    </span>
                                    {plan.priceEur && (
                                        <div className={`text-xs mt-1 ${plan.highlight ? 'text-indigo-200' : 'text-gray-400'}`}>
                                            <span className="text-blue-500">{plan.priceEur}</span>
                                            {' / '}
                                            <span className="text-emerald-500">{plan.priceUsd}</span>
                                        </div>
                                    )}
                                </div>
                                <ul className="space-y-2 mb-6">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className={`flex items-center gap-2 ${plan.highlight ? 'text-indigo-100' : 'text-gray-600'}`}>
                                            <span className={plan.highlight ? 'text-white' : 'text-green-500'}>✓</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={onGetStarted}
                                    className={`w-full py-3 rounded-xl font-semibold transition ${plan.highlight
                                        ? 'bg-white text-indigo-600 hover:bg-indigo-50'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        }`}
                                >
                                    {plan.cta}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-indigo-600 to-blue-700">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Prêt à moderniser votre atelier ?
                    </h2>
                    <p className="text-lg text-indigo-100 mb-8">
                        Rejoignez les artisans qui gagnent du temps chaque jour avec Carnet de Mesures
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={onGetStarted}
                            className="w-full sm:w-auto px-8 py-4 bg-white text-indigo-600 text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                        >
                            Créer mon compte gratuit
                        </button>
                        {isInstallPromptAvailable && (
                            <button
                                onClick={onInstallClick}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur text-white text-lg font-semibold rounded-2xl border-2 border-white/30 hover:border-white/60 transition-all"
                            >
                                <span className="text-2xl">📱</span> Installer l'app
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 bg-gray-900 text-gray-400">
                <div className="max-w-6xl mx-auto text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <img src="/logo.svg" alt="Logo" className="w-8 h-8 rounded-lg" />
                        <span className="font-bold text-white">Carnet de Mesures</span>
                    </div>
                    <p className="text-sm mb-4">
                        L'application des tailleurs et couturiers modernes
                    </p>
                    <p className="text-xs">
                        © 2025 Carnet de Mesures. Tous droits réservés.
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                        <a href="https://wa.me/2290198538343" className="hover:text-white transition">WhatsApp</a>
                        <span>•</span>
                        <a href="mailto:exponentiel29@gmail.com" className="hover:text-white transition">Contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};
